import * as React from "react"
import { X, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

export type AttachmentState = "idle" | "uploading" | "processing" | "error" | "done"
export type AttachmentSize = "default" | "sm" | "xs"
export type AttachmentOrientation = "horizontal" | "vertical"

export interface AttachmentProps extends React.HTMLAttributes<HTMLDivElement> {
  state?: AttachmentState
  size?: AttachmentSize
  orientation?: AttachmentOrientation
}

const Attachment = React.forwardRef<HTMLDivElement, AttachmentProps>(
  (
    {
      className,
      state = "idle",
      size = "default",
      orientation = "horizontal",
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        data-state={state}
        data-size={size}
        data-orientation={orientation}
        className={cn(
          "relative group flex rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#181C24] transition-all overflow-hidden",
          orientation === "horizontal"
            ? "flex-row items-center gap-3"
            : "flex-col items-stretch gap-2",
          size === "default" && "p-2.5 text-sm",
          size === "sm" && "p-2 text-xs",
          size === "xs" && "p-1.5 text-[11px]",
          state === "error" && "border-red-500/50 dark:border-red-500/50 bg-red-50/50 dark:bg-red-950/20",
          state === "uploading" && "opacity-90",
          className
        )}
        {...props}
      >
        {children}
        {state === "uploading" && (
          <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
            <Loader2 className="size-4 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
        )}
      </div>
    )
  }
)
Attachment.displayName = "Attachment"

export interface AttachmentMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "image" | "icon" | "default"
  src?: string
  alt?: string
}

const AttachmentMedia = React.forwardRef<HTMLDivElement, AttachmentMediaProps>(
  ({ className, variant = "default", src, alt = "Attachment preview", children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "relative shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-[#13161C] flex items-center justify-center border border-slate-200/60 dark:border-slate-800/80",
          variant === "image" ? "size-10 sm:size-12" : "size-9",
          className
        )}
        {...props}
      >
        {variant === "image" && src ? (
          <img src={src} alt={alt} className="w-full h-full object-cover" />
        ) : (
          children
        )}
      </div>
    )
  }
)
AttachmentMedia.displayName = "AttachmentMedia"

export interface AttachmentContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const AttachmentContent = React.forwardRef<HTMLDivElement, AttachmentContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex-1 min-w-0 flex flex-col justify-center", className)}
        {...props}
      />
    )
  }
)
AttachmentContent.displayName = "AttachmentContent"

export interface AttachmentTitleProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const AttachmentTitle = React.forwardRef<HTMLParagraphElement, AttachmentTitleProps>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(
          "font-medium text-slate-900 dark:text-slate-100 truncate text-xs sm:text-sm leading-tight",
          className
        )}
        {...props}
      />
    )
  }
)
AttachmentTitle.displayName = "AttachmentTitle"

export interface AttachmentDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const AttachmentDescription = React.forwardRef<HTMLParagraphElement, AttachmentDescriptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(
          "text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5",
          className
        )}
        {...props}
      />
    )
  }
)
AttachmentDescription.displayName = "AttachmentDescription"

export interface AttachmentActionsProps extends React.HTMLAttributes<HTMLDivElement> {}

const AttachmentActions = React.forwardRef<HTMLDivElement, AttachmentActionsProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-1 shrink-0", className)}
        {...props}
      />
    )
  }
)
AttachmentActions.displayName = "AttachmentActions"

export interface AttachmentActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "remove" | "default"
}

const AttachmentAction = React.forwardRef<HTMLButtonElement, AttachmentActionProps>(
  ({ className, variant = "default", children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "size-7 rounded-full flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:pointer-events-none disabled:opacity-50",
          variant === "remove"
            ? "text-slate-400 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
            : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
          className
        )}
        {...props}
      >
        {children ?? (variant === "remove" ? <X className="size-3.5" /> : null)}
      </button>
    )
  }
)
AttachmentAction.displayName = "AttachmentAction"

export interface AttachmentGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

const AttachmentGroup = React.forwardRef<HTMLDivElement, AttachmentGroupProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-2 overflow-x-auto py-1 scrollbar-none",
          className
        )}
        {...props}
      />
    )
  }
)
AttachmentGroup.displayName = "AttachmentGroup"

export {
  Attachment,
  AttachmentMedia,
  AttachmentContent,
  AttachmentTitle,
  AttachmentDescription,
  AttachmentActions,
  AttachmentAction,
  AttachmentGroup,
}
